import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { Plus, Trash2 } from 'lucide-react';
import { SPECIES } from '@zt/content';
import {
  Badge,
  Button,
  EmptyState,
  Field,
  Input,
  Section,
  Select,
  Spinner,
  Textarea,
  buttonStyles,
} from '@zt/ui';
import { ConfirmButton } from '../components/Confirm';
import { ProductMultiPicker } from '../components/ProductMultiPicker';
import { api } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { CollectionDetail, CollectionMatches, CollectionRow, ProductTag } from '../lib/types';
import { useAttributeDefs } from './attributes';
import { slugify } from './brands';
import { statusTone, useCatalogRefs } from './products';

/**
 * Koleksiyonlar (F5B-01): elle seçilen ürünler veya kurala uyan ürünler (kategori, marka, etiket, tür,
 * özellik). Kurallı koleksiyon her sorguda yeniden hesaplanır.
 */

type Condition = {
  field: 'category' | 'brand' | 'tag' | 'species' | 'attribute';
  op: 'is' | 'is_not' | 'gte' | 'lte';
  value: string;
  attributeId?: string;
};
const FIELDS: Condition['field'][] = ['category', 'brand', 'tag', 'species', 'attribute'];

export function CollectionsPage() {
  const t = useT();
  const { formatNumber } = useI18n();
  const { can } = usePermissions();
  const q = useQuery({
    queryKey: ['collections'],
    queryFn: () => api.get<CollectionRow[]>('/v1/admin/collections'),
  });
  return (
    <PageFrame
      helpKey="page.collections"
      actions={
        can('catalog.product.write') && (
          <Link
            to="/koleksiyonlar/$id"
            params={{ id: 'yeni' }}
            className={buttonStyles({ variant: 'primary' })}
          >
            {t('admin.collections.add')}
          </Link>
        )
      }
    >
      <Section title={t('admin.collections.title')} description={t('admin.collections.lead')}>
        {!q.data ? (
          <Spinner label={t('common.loading')} />
        ) : q.data.length === 0 ? (
          <EmptyState
            title={t('admin.collections.emptyTitle')}
            description={t('admin.collections.emptyLead')}
          />
        ) : (
          <div className="overflow-x-auto rounded-md border border-line">
            <table className="w-full text-sm" aria-label={t('admin.collections.title')}>
              <thead className="bg-surface-2 text-ink-2">
                <tr>
                  <th className="px-3 py-2 text-start font-medium">{t('admin.collections.name')}</th>
                  <th className="px-3 py-2 text-start font-medium">{t('admin.collections.kind')}</th>
                  <th className="px-3 py-2 text-start font-medium">{t('admin.collections.status')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('admin.collections.productCount')}</th>
                </tr>
              </thead>
              <tbody>
                {q.data.map((c) => (
                  <tr key={c.id} className="border-t border-line">
                    <td className="px-3 py-2">
                      <Link
                        to="/koleksiyonlar/$id"
                        params={{ id: c.id }}
                        className="font-medium text-pine underline-offset-2 hover:underline"
                      >
                        {c.title}
                      </Link>
                      <span className="block font-mono text-xs text-ink-3">/koleksiyon/{c.slug}</span>
                    </td>
                    <td className="px-3 py-2">{t(`admin.collections.kinds.${c.kind}`)}</td>
                    <td className="px-3 py-2">
                      <Badge tone={c.status === 'active' ? 'moss' : 'slate'}>
                        {t(`admin.collections.statuses.${c.status}`)}
                      </Badge>
                    </td>
                    <td className="px-3 py-2 text-end tabular-nums">{formatNumber(c.productCount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Section>
    </PageFrame>
  );
}

export function CollectionEditorPage() {
  const t = useT();
  const { id } = useParams({ from: '/app/koleksiyonlar/$id' });
  const isNew = id === 'yeni';
  const q = useQuery({
    queryKey: ['collection', id],
    queryFn: () => api.get<CollectionDetail>(`/v1/admin/collections/${id}`),
    enabled: !isNew,
  });
  if (!isNew && !q.data) return <Spinner label={t('common.loading')} />;
  return <CollectionEditor key={q.data?.version ?? 'new'} row={isNew ? null : (q.data ?? null)} />;
}

function CollectionEditor({ row }: { row: CollectionDetail | null }) {
  const t = useT();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const canWrite = can('catalog.product.write');
  const [f, setF] = useState({
    title: row?.title ?? '',
    slug: row?.slug ?? '',
    slugTouched: !!row,
    description: row?.description ?? '',
    seoTitle: row?.seoTitle ?? '',
    seoDescription: row?.seoDescription ?? '',
    kind: (row?.kind ?? 'manual') as 'manual' | 'rule',
    status: (row?.status ?? 'active') as 'active' | 'hidden',
    match: (row?.rules.match ?? 'all') as 'all' | 'any',
    conditions: (row?.rules.conditions ?? []) as Condition[],
    productIds: row?.productIds ?? [],
  });
  const [preview, setPreview] = useState<CollectionMatches | null>(null);
  const opt = (s: string) => (s.trim() ? s.trim() : null);
  const body = {
    title: f.title,
    ...(f.slug.trim() ? { slug: f.slug.trim() } : {}),
    description: opt(f.description),
    seoTitle: opt(f.seoTitle),
    seoDescription: opt(f.seoDescription),
    kind: f.kind,
    status: f.status,
    ...(f.kind === 'rule'
      ? { rules: { match: f.match, conditions: f.conditions } }
      : { productIds: f.productIds }),
  };
  const save = useApiMutation(
    () =>
      row
        ? api.patch<{ id: string }>(`/v1/admin/collections/${row.id}`, { ...body, version: row.version })
        : api.post<{ id: string }>('/v1/admin/collections', body),
    {
      invalidate: [['collections'], ['collection']],
      success: t('admin.collections.saved'),
      silentFieldErrors: true,
      onSuccess: (r) => {
        if (!row) void navigate({ to: '/koleksiyonlar/$id', params: { id: r.id } });
      },
    },
  );
  const runPreview = useApiMutation(
    () =>
      api.post<CollectionMatches>('/v1/admin/collections/preview', {
        rules: { match: f.match, conditions: f.conditions },
      }),
    { silentFieldErrors: true, onSuccess: (r) => setPreview(r) },
  );
  const del = useApiMutation(() => api.del(`/v1/admin/collections/${row!.id}`), {
    invalidate: [['collections']],
    success: t('admin.collections.deleted'),
    onSuccess: () => void navigate({ to: '/koleksiyonlar' }),
  });
  const err = (k: string) => fieldError(save.error, k) ?? fieldError(runPreview.error, k);
  const matches = preview ?? row?.matches ?? null;
  return (
    <PageFrame
      helpKey="page.collection_editor"
      title={row ? row.title : t('admin.collections.add')}
      actions={
        <>
          <Link to="/koleksiyonlar" className={buttonStyles({ variant: 'secondary' })}>
            {t('admin.collections.backToList')}
          </Link>
          {canWrite && row && (
            <ConfirmButton
              variant="ghost"
              message={t('admin.collections.deleteConfirm', { title: row.title })}
              onConfirm={() => del.mutateAsync(undefined)}
            >
              {t('common.delete')}
            </ConfirmButton>
          )}
          {canWrite && (
            <Button variant="primary" loading={save.isPending} onClick={() => save.mutate(undefined)}>
              {t('common.save')}
            </Button>
          )}
        </>
      }
    >
      <Section title={t('admin.collections.basics')}>
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label={t('admin.collections.name')} required error={err('title')} className="sm:col-span-2">
            {(p) => (
              <Input
                {...p}
                value={f.title}
                maxLength={120}
                disabled={!canWrite}
                onChange={(e) =>
                  setF({
                    ...f,
                    title: e.target.value,
                    slug: f.slugTouched ? f.slug : slugify(e.target.value),
                  })
                }
              />
            )}
          </Field>
          <Field
            label={t('admin.collections.slug')}
            error={err('slug')}
            help={<HelpTip k="collections.slug" />}
          >
            {(p) => (
              <Input
                {...p}
                value={f.slug}
                maxLength={80}
                disabled={!canWrite}
                onChange={(e) => setF({ ...f, slug: e.target.value, slugTouched: true })}
              />
            )}
          </Field>
          <Field label={t('admin.collections.status')} help={<HelpTip k="collections.status" />}>
            {(p) => (
              <Select
                {...p}
                value={f.status}
                disabled={!canWrite}
                onChange={(e) => setF({ ...f, status: e.target.value as 'active' | 'hidden' })}
              >
                <option value="active">{t('admin.collections.statuses.active')}</option>
                <option value="hidden">{t('admin.collections.statuses.hidden')}</option>
              </Select>
            )}
          </Field>
          <Field
            label={t('admin.collections.description')}
            optionalLabel={t('common.optional')}
            className="sm:col-span-2"
          >
            {(p) => (
              <Textarea
                {...p}
                rows={3}
                value={f.description}
                maxLength={5000}
                disabled={!canWrite}
                onChange={(e) => setF({ ...f, description: e.target.value })}
              />
            )}
          </Field>
          <Field
            label={t('admin.collections.seoTitle')}
            optionalLabel={t('common.optional')}
            error={err('seoTitle')}
          >
            {(p) => (
              <Input
                {...p}
                value={f.seoTitle}
                maxLength={70}
                disabled={!canWrite}
                onChange={(e) => setF({ ...f, seoTitle: e.target.value })}
              />
            )}
          </Field>
          <Field
            label={t('admin.collections.seoDescription')}
            optionalLabel={t('common.optional')}
            error={err('seoDescription')}
          >
            {(p) => (
              <Input
                {...p}
                value={f.seoDescription}
                maxLength={170}
                disabled={!canWrite}
                onChange={(e) => setF({ ...f, seoDescription: e.target.value })}
              />
            )}
          </Field>
        </div>
      </Section>

      <Section title={t('admin.collections.productsTitle')} action={<HelpTip k="collections.kind" />}>
        <fieldset className="mb-4 flex flex-wrap gap-x-6 gap-y-2">
          <legend className="sr-only">{t('admin.collections.kind')}</legend>
          {(['manual', 'rule'] as const).map((k) => (
            <label key={k} className="flex items-center gap-2 text-sm">
              <input
                type="radio"
                name="collection-kind"
                className="accent-[var(--zt-pine)]"
                checked={f.kind === k}
                disabled={!canWrite}
                onChange={() => {
                  setF({ ...f, kind: k });
                  setPreview(null);
                }}
              />
              {t(`admin.collections.kindChoice.${k}`)}
            </label>
          ))}
        </fieldset>
        {f.kind === 'manual' ? (
          <ProductMultiPicker
            label={t('admin.collections.pickProducts')}
            help={<HelpTip k="collections.manual" />}
            value={f.productIds}
            onChange={(ids) => setF({ ...f, productIds: ids })}
            error={err('productIds')}
            max={500}
          />
        ) : (
          <RuleBuilder
            match={f.match}
            conditions={f.conditions}
            disabled={!canWrite}
            onChange={(match, conditions) => {
              setF({ ...f, match, conditions });
              setPreview(null);
            }}
            error={(i) => err(`rules.conditions.${i}`)}
            listError={err('rules.conditions')}
            onPreview={() => runPreview.mutate(undefined)}
            previewing={runPreview.isPending}
          />
        )}
      </Section>

      {matches && (
        <Section
          title={t('admin.collections.matchesTitle', { count: matches.count })}
          description={preview ? t('admin.collections.previewLead') : t('admin.collections.matchesLead')}
        >
          {matches.items.length === 0 ? (
            <p className="text-sm text-ink-3">{t('admin.collections.noMatches')}</p>
          ) : (
            <ul className="grid gap-1 text-sm">
              {matches.items.map((p) => (
                <li key={p.id} className="flex items-center gap-2">
                  <Link
                    to="/urunler/$id"
                    params={{ id: p.id }}
                    className="text-pine underline-offset-2 hover:underline"
                  >
                    {p.title}
                  </Link>
                  <Badge tone={statusTone[p.status] ?? 'neutral'}>
                    {t(`admin.products.statuses.${p.status}`)}
                  </Badge>
                </li>
              ))}
            </ul>
          )}
        </Section>
      )}
    </PageFrame>
  );
}

function RuleBuilder({
  match,
  conditions,
  disabled,
  onChange,
  error,
  listError,
  onPreview,
  previewing,
}: {
  match: 'all' | 'any';
  conditions: Condition[];
  disabled: boolean;
  onChange: (match: 'all' | 'any', conditions: Condition[]) => void;
  error: (i: number) => string | undefined;
  listError?: string;
  onPreview: () => void;
  previewing: boolean;
}) {
  const t = useT();
  const { brands, categories } = useCatalogRefs();
  const attrs = useAttributeDefs().data ?? [];
  const tags = useQuery({
    queryKey: ['product-tags'],
    queryFn: () => api.get<ProductTag[]>('/v1/admin/product-tags'),
  });
  const setAt = (i: number, c: Condition) =>
    onChange(
      match,
      conditions.map((x, j) => (j === i ? c : x)),
    );
  const valueInput = (c: Condition, i: number) => {
    const common = {
      'aria-label': t('admin.collections.value'),
      disabled,
      value: c.value,
    };
    if (c.field === 'category')
      return (
        <Select {...common} onChange={(e) => setAt(i, { ...c, value: e.target.value })}>
          <option value="">{t('admin.collections.choose')}</option>
          {categories.map((x) => (
            <option key={x.id} value={x.id}>
              {x.name}
            </option>
          ))}
        </Select>
      );
    if (c.field === 'brand')
      return (
        <Select {...common} onChange={(e) => setAt(i, { ...c, value: e.target.value })}>
          <option value="">{t('admin.collections.choose')}</option>
          {brands.map((x) => (
            <option key={x.id} value={x.id}>
              {x.name}
            </option>
          ))}
        </Select>
      );
    if (c.field === 'species')
      return (
        <Select {...common} onChange={(e) => setAt(i, { ...c, value: e.target.value })}>
          <option value="">{t('admin.collections.choose')}</option>
          {SPECIES.map((s) => (
            <option key={s} value={s}>
              {t(`admin.products.speciesNames.${s}`)}
            </option>
          ))}
        </Select>
      );
    if (c.field === 'tag')
      return (
        <>
          <Input
            {...common}
            list="collection-tags"
            maxLength={40}
            onChange={(e) => setAt(i, { ...c, value: e.target.value })}
          />
          <datalist id="collection-tags">
            {(tags.data ?? []).map((x) => (
              <option key={x.tag} value={x.tag} />
            ))}
          </datalist>
        </>
      );
    const def = attrs.find((a) => a.id === c.attributeId);
    if (def && (def.type === 'select' || def.type === 'multiselect'))
      return (
        <Select {...common} onChange={(e) => setAt(i, { ...c, value: e.target.value })}>
          <option value="">{t('admin.collections.choose')}</option>
          {def.options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </Select>
      );
    if (def?.type === 'boolean')
      return (
        <Select {...common} onChange={(e) => setAt(i, { ...c, value: e.target.value })}>
          <option value="">{t('admin.collections.choose')}</option>
          <option value="true">{t('common.yes')}</option>
          <option value="false">{t('common.no')}</option>
        </Select>
      );
    return (
      <Input
        {...common}
        inputMode={def?.type === 'number' ? 'decimal' : undefined}
        maxLength={100}
        onChange={(e) => setAt(i, { ...c, value: e.target.value })}
      />
    );
  };
  return (
    <div className="space-y-3">
      <Field label={t('admin.collections.match')} help={<HelpTip k="collections.match" />}>
        {(p) => (
          <Select
            {...p}
            value={match}
            disabled={disabled}
            onChange={(e) => onChange(e.target.value as 'all' | 'any', conditions)}
          >
            <option value="all">{t('admin.collections.matchAll')}</option>
            <option value="any">{t('admin.collections.matchAny')}</option>
          </Select>
        )}
      </Field>
      <ol className="space-y-2" aria-label={t('admin.collections.conditions')}>
        {conditions.map((c, i) => {
          const def = attrs.find((a) => a.id === c.attributeId);
          const ops: Condition['op'][] =
            c.field === 'attribute' && def?.type === 'number'
              ? ['is', 'is_not', 'gte', 'lte']
              : ['is', 'is_not'];
          return (
            <li key={i} className="rounded-md border border-line p-3">
              <div className="grid gap-2 sm:grid-cols-[10rem_10rem_8rem_1fr_auto]">
                <Select
                  aria-label={t('admin.collections.field')}
                  value={c.field}
                  disabled={disabled}
                  onChange={(e) =>
                    setAt(i, { field: e.target.value as Condition['field'], op: 'is', value: '' })
                  }
                >
                  {FIELDS.map((x) => (
                    <option key={x} value={x}>
                      {t(`admin.collections.fields.${x}`)}
                    </option>
                  ))}
                </Select>
                {c.field === 'attribute' ? (
                  <Select
                    aria-label={t('admin.collections.attribute')}
                    value={c.attributeId ?? ''}
                    disabled={disabled}
                    onChange={(e) => setAt(i, { ...c, attributeId: e.target.value, op: 'is', value: '' })}
                  >
                    <option value="">{t('admin.collections.choose')}</option>
                    {attrs.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.name}
                      </option>
                    ))}
                  </Select>
                ) : (
                  <span aria-hidden className="hidden sm:block" />
                )}
                <Select
                  aria-label={t('admin.collections.op')}
                  value={c.op}
                  disabled={disabled}
                  onChange={(e) => setAt(i, { ...c, op: e.target.value as Condition['op'] })}
                >
                  {ops.map((o) => (
                    <option key={o} value={o}>
                      {t(`admin.collections.ops.${o}`)}
                    </option>
                  ))}
                </Select>
                {valueInput(c, i)}
                {!disabled && (
                  <Button
                    size="icon"
                    variant="ghost"
                    aria-label={t('admin.collections.removeCondition')}
                    onClick={() =>
                      onChange(
                        match,
                        conditions.filter((_, j) => j !== i),
                      )
                    }
                  >
                    <Trash2 className="h-4 w-4" aria-hidden />
                  </Button>
                )}
              </div>
              {error(i) && (
                <p role="alert" className="mt-1 text-sm text-rose">
                  {error(i)}
                </p>
              )}
            </li>
          );
        })}
      </ol>
      {listError && (
        <p role="alert" className="text-sm text-rose">
          {listError}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {!disabled && conditions.length < 10 && (
          <Button
            size="sm"
            onClick={() => onChange(match, [...conditions, { field: 'category', op: 'is', value: '' }])}
          >
            <Plus className="h-4 w-4" aria-hidden />
            {t('admin.collections.addCondition')}
          </Button>
        )}
        <Button size="sm" loading={previewing} onClick={onPreview} disabled={!conditions.length}>
          {t('admin.collections.preview')}
        </Button>
        <HelpTip k="collections.preview" />
      </div>
    </div>
  );
}
